import {
	boolean,
	date,
	index,
	integer,
	pgEnum,
	pgTable,
	text,
	timestamp,
	uniqueIndex,
	uuid
} from "drizzle-orm/pg-core";

import { baseTableProps } from "../../baseTableProps";
import { displayNameProps } from "../_shared/display-name";

import { companies } from "../companies/companies.model";
import { teamMembers } from "../members/members.model";

export const financialReviewCycleStatusEnum = pgEnum("financial_review_cycle_status", [
	"awaiting_submission",
	"in_review",
	"final_review",
	"complete"
]);

export const financialReviewParseStatusEnum = pgEnum("financial_review_parse_status", [
	"pending",
	"processed",
	"failed"
]);

export const finrevIssueSyncStatusEnum = pgEnum("finrev_issue_sync_status", [
	"pending",
	"synced",
	"error",
	"deleted"
]);

export const financialReviewTemplates = pgTable("financial_review_templates", {
	...baseTableProps,
	...displayNameProps,
	name: text("name").notNull()
});

export const financialReviewTemplateSteps = pgTable(
	"financial_review_template_steps",
	{
		...baseTableProps,
		templateId: uuid("template_id")
			.references(() => financialReviewTemplates.id, { onDelete: "cascade" })
			.notNull(),
		name: text("name").notNull(),
		orderIndex: integer("order_index").notNull().default(0),
		targetDays: integer("target_days").notNull().default(5)
	},
	(table) => [
		index("financial_review_template_steps_template_idx").on(table.templateId),
		uniqueIndex("financial_review_template_steps_order_uq").on(
			table.templateId,
			table.orderIndex
		)
	]
);

export const financialReviewStepReviewers = pgTable(
	"financial_review_step_reviewers",
	{
		...baseTableProps,
		stepId: uuid("step_id")
			.references(() => financialReviewTemplateSteps.id, { onDelete: "cascade" })
			.notNull(),
		reviewerId: uuid("reviewer_id")
			.references(() => teamMembers.id, { onDelete: "restrict" })
			.notNull()
	},
	(table) => [
		index("financial_review_step_reviewers_step_idx").on(table.stepId),
		uniqueIndex("financial_review_step_reviewers_identity_uq").on(
			table.stepId,
			table.reviewerId
		)
	]
);

export const companyFinancials = pgTable(
	"company_financials",
	{
		...baseTableProps,
		...displayNameProps,
		companyId: uuid("company_id")
			.references(() => companies.id, { onDelete: "cascade" })
			.notNull(),
		reviewTemplateId: uuid("review_template_id")
			.references(() => financialReviewTemplates.id, { onDelete: "restrict" })
			.notNull(),
		periodStart: date("period_start", { mode: "string" }).notNull(),
		periodEnd: date("period_end", { mode: "string" }).notNull(),
		status: financialReviewCycleStatusEnum("status").notNull().default("awaiting_submission"),
		backfilled: boolean("backfilled").notNull().default(false),
		legacyStatusOverride: text("legacy_status_override"),
		asanaMonthTaskGid: text("asana_month_task_gid")
	},
	(table) => [
		uniqueIndex("company_financials_company_period_uq").on(table.companyId, table.periodStart),
		index("company_financials_period_idx").on(table.periodStart, table.periodEnd),
		index("company_financials_status_idx").on(table.status)
	]
);

export const financialSetVersions = pgTable(
	"financial_set_versions",
	{
		...baseTableProps,
		cycleId: uuid("cycle_id")
			.references(() => companyFinancials.id, { onDelete: "cascade" })
			.notNull(),
		versionNumber: integer("version_number").notNull(),
		uploadedById: uuid("uploaded_by_id").references(() => teamMembers.id, {
			onDelete: "set null"
		}),
		isFinal: boolean("is_final").notNull().default(false),
		parseStatus: financialReviewParseStatusEnum("parse_status").notNull().default("pending"),
		parseError: text("parse_error"),
		parsedAt: timestamp("parsed_at", { withTimezone: true, mode: "string" })
	},
	(table) => [
		index("financial_set_versions_cycle_idx").on(table.cycleId),
		uniqueIndex("financial_set_versions_cycle_number_uq").on(table.cycleId, table.versionNumber)
	]
);

export const financialReviewRecords = pgTable(
	"financial_review_records",
	{
		...baseTableProps,
		cycleId: uuid("cycle_id")
			.references(() => companyFinancials.id, { onDelete: "cascade" })
			.notNull(),
		stepId: uuid("step_id")
			.references(() => financialReviewTemplateSteps.id, { onDelete: "restrict" })
			.notNull(),
		reviewerId: uuid("reviewer_id")
			.references(() => teamMembers.id, { onDelete: "restrict" })
			.notNull(),
		setVersionId: uuid("set_version_id")
			.references(() => financialSetVersions.id, { onDelete: "restrict" })
			.notNull(),
		completedAt: timestamp("completed_at", { withTimezone: true, mode: "string" })
			.notNull()
			.defaultNow()
	},
	(table) => [
		index("financial_review_records_cycle_idx").on(table.cycleId),
		index("financial_review_records_step_idx").on(table.stepId),
		uniqueIndex("financial_review_records_identity_uq").on(
			table.cycleId,
			table.stepId,
			table.reviewerId
		)
	]
);

export const finrevIssues = pgTable(
	"finrev_issues",
	{
		...baseTableProps,
		...displayNameProps,
		cycleId: uuid("cycle_id")
			.references(() => companyFinancials.id, { onDelete: "cascade" })
			.notNull(),
		raisedSetVersionId: uuid("raised_set_version_id").references(
			() => financialSetVersions.id,
			{
				onDelete: "set null"
			}
		),
		title: text("title").notNull(),
		description: text("description").notNull().default(""),
		asanaSubtaskGid: text("asana_subtask_gid"),
		syncStatus: finrevIssueSyncStatusEnum("sync_status").notNull().default("pending"),
		lastSyncAt: timestamp("last_sync_at", { withTimezone: true, mode: "string" }),
		syncError: text("sync_error"),
		completed: boolean("completed").notNull().default(false)
	},
	(table) => [
		index("finrev_issues_cycle_idx").on(table.cycleId),
		uniqueIndex("finrev_issues_asana_subtask_gid_uq").on(table.asanaSubtaskGid)
	]
);
